import axios, { AxiosError } from "axios";
import { StatusCodes } from "http-status-codes";
import qs from "qs";
import config from "../config/config";
import { clearLogin, getSessionEmail, setSession, storeReturnUrl } from "./auth";
import { AvailableBaseRoutes, isLoginRoute } from "./constants";

/** Axios lower-cases response header names. */
const RENEWED_TOKEN_HEADER = "x-renewed-token";

/**
 * @deprecated use a real typed interface
 */
export interface ILooseObject {
  [key: string]: any;
}

const defaultAPIConfig: ILooseObject = {};
const defaultAuth: ILooseObject = {};

const { api = defaultAPIConfig, auth = defaultAuth } = config;
const { baseURL } = api;
const { tokenKey } = auth;

const getInstance = (headers = {}, props = {}) => {
  const token: string = localStorage.getItem(tokenKey) || "";

  let heads: ILooseObject = {};

  if (token) {
    heads = { Authorization: `Bearer ${token}`, ...headers };
  }

  const axiosInstance = axios.create({
    baseURL,
    headers: heads,
    // required to send cookies
    withCredentials: true,
    paramsSerializer: (params) => qs.stringify(params),
    ...props,
  });

  // Adopt sliding-renewal tokens, and log out on 401 to handle expired sessions.
  axiosInstance.interceptors.response.use(
    (response) => {
      const renewedToken = response.headers[RENEWED_TOKEN_HEADER];
      if (renewedToken) {
        setSession(renewedToken);
      }
      return response;
    },
    (error) => {
      // `error.response` is undefined for network errors — guard before reading it.
      const { status, headers } = error.response ?? {};

      if (status === StatusCodes.UNAUTHORIZED) {
        // Read before clearLogin() drops the token, so the remembered location can only
        // be handed back to the same login.
        const nextOwner = getSessionEmail();
        clearLogin();
        if (!isLoginRoute(window.location.pathname)) {
          // Remembered only when we are actually leaving a page. A failed login
          // attempt 401s too, and storing the login page here would send the next
          // successful login straight back to it.
          const next = window.location.pathname + window.location.search + window.location.hash;
          storeReturnUrl({ next, nextOwner });
          // assign(), not `.pathname =`, which would keep this page's query and hash.
          window.location.assign(AvailableBaseRoutes.Login);
        }
      } else if (headers?.[RENEWED_TOKEN_HEADER]) {
        // Renewal happens in the auth middleware, before the handler runs, so a
        // request that authorizes fine but then 403s or 404s still carries a
        // renewed token. Without this it would be dropped and a user whose
        // traffic is mostly errors would never renew.
        //
        // 401 is deliberately excluded: that is the logout path and clearLogin()
        // must win. A 401 cannot carry a renewed token anyway, since renewal only
        // runs after every auth check has passed.
        setSession(headers[RENEWED_TOKEN_HEADER]);
      }

      return Promise.reject(error);
    },
  );

  return axiosInstance;
};

export const getInstanceMultipartFormData = () =>
  getInstance({
    "Content-Type": "multipart/form-data",
  });

export function instanceOfAxiosError(object: unknown): object is AxiosError {
  return typeof object === "object" && object !== null && "isAxiosError" in object;
}

export default getInstance;
